/**
 * PendingRecapBadge — Iter-220j (Sprint 1A).
 *
 * Surfaces a reminder card on the talent home when one or more shifts
 * are in the `Post-Event Recap Required` state. Tapping the card opens
 * the PostEventRecapModal pre-filled with the most-recent pending
 * booking. The card stays visible until every pending recap is
 * submitted (or until the soft-block kicks in after the threshold).
 *
 * Mobile-first: full-width card, large tap target, prominent count.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ClipboardCheck, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import PostEventRecapModal from './PostEventRecapModal';

export const PendingRecapBadge = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openBooking, setOpenBooking] = useState(null);
  const [softBlockReason, setSoftBlockReason] = useState(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, applyRes] = await Promise.all([
        api.get('/post-event-recap/pending'),
        api.get('/post-event-recap/can-apply').catch(() => ({ data: { can_apply: true } })),
      ]);
      setPending(pendingRes?.data?.bookings || []);
      setSoftBlockReason(
        applyRes?.data?.can_apply === false ? applyRes.data.reason : null
      );
    } catch (_e) {
      // Endpoint failure — silent (we don't want a UI fight on home).
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  if (loading || pending.length === 0) return null;

  const next = pending[0];

  return (
    <>
      <Card
        className="bg-amber-50 border-amber-200 border-l-4 border-l-amber-500"
        data-testid="pending-recap-card"
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <ClipboardCheck className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="font-semibold text-amber-900 text-sm">
                  {pending.length === 1
                    ? 'Event Recap Required'
                    : `${pending.length} Event Recaps Required`}
                </h3>
                <span
                  className="text-[10px] font-medium bg-amber-200 text-amber-900 rounded-full px-2 py-0.5"
                  data-testid="pending-recap-count"
                >
                  {pending.length}
                </span>
              </div>
              <p className="text-xs text-amber-800 leading-snug mb-2">
                {pending.length === 1
                  ? `Submit your recap for "${next.shift_title || 'your shift'}" to unlock payment for this booking.`
                  : `Submit your pending recaps to unlock payment for these bookings.`}
              </p>
              {softBlockReason && (
                <div
                  className="rounded-md border border-rose-300 bg-rose-50 text-rose-800 text-[11px] p-2 mb-2 flex items-start gap-1.5"
                  data-testid="pending-recap-soft-block"
                >
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{softBlockReason}</span>
                </div>
              )}
              <Button
                size="sm"
                onClick={() => setOpenBooking(next)}
                className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-4"
                data-testid="pending-recap-open-btn"
              >
                Complete recap
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <PostEventRecapModal
        isOpen={!!openBooking}
        onClose={() => setOpenBooking(null)}
        booking={openBooking}
        onRecapSubmitted={() => {
          setOpenBooking(null);
          fetchPending();
        }}
      />
    </>
  );
};

export default PendingRecapBadge;
